import { notify } from '@/actions/ui';
import type { AppDispatch } from '@/store';
import { getCache, setCache } from '@/utils';

// A sign-in ends in a full reload, which drops a snackbar. Notices for the
// user wait here and the next boot shows them once: the invitation that could
// not be accepted (ENG-6105) and the provider account just connected
// (ENG-6192).
const key = 'post_login_notices';

export interface PostLoginNotice {
  message: string;
  type: 'success' | 'error';
}

function isNotice(value: unknown): value is PostLoginNotice {
  if (typeof value !== 'object' || value === null) return false;
  const { message, type } = value as Partial<PostLoginNotice>;
  return typeof message === 'string' && (type === 'success' || type === 'error');
}

// This runs at boot and during sign-in, so storage that is not JSON counts as
// empty and is cleared rather than thrown.
function savedNotices(): PostLoginNotice[] {
  let saved: unknown;
  try {
    saved = getCache(key);
  } catch {
    setCache(key, undefined);
    return [];
  }
  return Array.isArray(saved) ? saved.filter(isNotice) : [];
}

export function savePostLoginNotice(message: string, type: PostLoginNotice['type']): void {
  const saved = savedNotices();
  if (saved.some((notice) => notice.message === message)) return;
  setCache(key, [...saved, { message, type }]);
}

export function takePostLoginNotices(): PostLoginNotice[] {
  const saved = savedNotices();
  setCache(key, undefined);
  return saved;
}

export function showPostLoginNotices(dispatch: AppDispatch): void {
  takePostLoginNotices().forEach(({ message, type }) => dispatch(notify(message, type)));
}
